// Desktop "Settings": an overlay opened from the account menu's "Settings" item
// (ui/auth-panel.ts). The overlay is a keyboard-shortcut reference where the
// Tools/Shape keys are REBINDABLE — click a key chip, press a new key — backed
// by hotkeys.ts. Other shortcuts (Cmd combos, view keys) are shown as a fixed,
// non-editable reference.

import {
  type HotkeyDef,
  allBindings,
  commitBindings,
  currentBindings,
  defaultBindings,
  draftSetBinding,
} from "./hotkeys";

// A fixed (non-editable) shortcut reference row + section.
export interface ShortcutItem {
  keys: string;
  label: string;
}
export interface ShortcutSection {
  group: string;
  items: ShortcutItem[];
}
export interface SettingsOptions {
  fixed: ShortcutSection[]; // non-editable reference sections (Edit, View, …)
  onChange: () => void; // called after a rebind so the caller can re-render UI
}

const CSS = `
.dt-settings {
  position: fixed; inset: 0; z-index: 60; display: flex; align-items: center;
  justify-content: center; background: rgba(0,0,0,0.45);
  font: 13px/1.45 system-ui, -apple-system, sans-serif; color: #eceff6;
}
.dt-settings .card {
  width: 440px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px);
  overflow-y: auto; padding: 16px 20px 20px; border-radius: 14px;
  background: rgba(28,30,38,0.98); border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
}
.dt-settings .head {
  display: flex; align-items: center; justify-content: space-between;
  position: sticky; top: -16px; background: rgba(28,30,38,0.98);
  padding-top: 4px; margin: -4px 0 2px;
}
.dt-settings h3 { margin: 0; font-size: 16px; font-weight: 650; }
.dt-settings .x {
  font: inherit; cursor: pointer; border: none; background: transparent;
  color: #aeb3c4; font-size: 18px; line-height: 1; padding: 4px 8px; border-radius: 6px;
}
.dt-settings .x:hover { background: rgba(255,255,255,0.08); color: #fff; }
.dt-settings .hint { color: #8b90a3; font-size: 11px; margin: 8px 0 2px; }
.dt-settings .sub {
  color: #8b90a3; font-size: 11px; text-transform: uppercase; letter-spacing: .05em;
  margin: 16px 0 4px;
}
.dt-settings .r {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 5px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
}
.dt-settings .r .lab { color: #c7ccda; }
.dt-settings kbd, .dt-settings .rebind {
  font: 600 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #e7e9f0;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.16);
  border-bottom-width: 2px; border-radius: 5px; padding: 3px 8px; white-space: nowrap;
}
.dt-settings .rebind { cursor: pointer; min-width: 56px; text-align: center; }
.dt-settings .rebind:hover { background: rgba(255,255,255,0.16); }
.dt-settings .rebind.listening { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
.dt-settings .rebind.unbound { color: #8b90a3; font-style: italic; }
.dt-settings .reset {
  margin-top: 12px; font: inherit; cursor: pointer; border-radius: 7px; padding: 6px 12px;
  border: 1px solid rgba(255,255,255,0.14); background: #34384a; color: #d7dae6;
}
.dt-settings .reset:hover { background: #424862; }
.dt-settings .head .right { display: flex; align-items: center; gap: 8px; }
.dt-settings .save {
  font: 600 12px system-ui, -apple-system, sans-serif; cursor: pointer;
  border-radius: 7px; padding: 5px 14px; border: 1px solid #6fa0ff; background: #6fa0ff;
  color: #11141c;
}
.dt-settings .save:hover { background: #8ab4ff; border-color: #8ab4ff; }
.dt-settings .save:disabled { opacity: 0.45; cursor: default; }
`;

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const s = document.createElement("style");
  s.textContent = CSS;
  document.head.appendChild(s);
  stylesInjected = true;
}

let openRoot: HTMLElement | null = null;

export function showSettings(opts: SettingsOptions): void {
  if (openRoot) return; // already showing
  injectStyles();

  const root = document.createElement("div");
  root.className = "dt-settings";
  openRoot = root;
  const card = document.createElement("div");
  card.className = "card";
  root.appendChild(card);
  document.body.appendChild(root);

  // Which binding (if any) is currently capturing its next keypress.
  let listeningId: string | null = null;
  // Edits happen on a DRAFT; the live bindings don't change until Save. Closing
  // the overlay any other way (✕, Esc, backdrop) just discards the draft.
  const seed = currentBindings();
  let draft = currentBindings();
  const isDirty = (): boolean =>
    allBindings().some(({ def }) => draft[def.id] !== seed[def.id]);

  const mkChip = (text: string): HTMLElement => {
    const kb = document.createElement("kbd");
    kb.textContent = text;
    return kb;
  };

  const rebuild = (): void => {
    card.replaceChildren();

    const head = document.createElement("div");
    head.className = "head";
    const h = document.createElement("h3");
    h.textContent = "Settings";
    const right = document.createElement("div");
    right.className = "right";
    const save = document.createElement("button");
    save.className = "save";
    save.textContent = "Save";
    save.title = "Apply changes and close";
    save.disabled = !isDirty();
    save.addEventListener("click", () => {
      commitBindings(draft);
      opts.onChange();
      close();
    });
    const x = document.createElement("button");
    x.className = "x";
    x.textContent = "✕";
    x.title = "Close without saving";
    x.addEventListener("click", close);
    right.append(save, x);
    head.append(h, right);
    card.appendChild(head);

    const hint = document.createElement("div");
    hint.className = "hint";
    hint.textContent = listeningId
      ? "Press a key…  (Esc cancels · Backspace clears)"
      : "Click a key to rebind. Changes apply when you click Save.";
    card.appendChild(hint);

    // Editable bindings (the DRAFT), grouped in declaration order.
    const groups: HotkeyDef["group"][] = ["Tools", "Shape", "Layout", "View", "Selection", "Camera"];
    for (const g of groups) {
      const sub = document.createElement("div");
      sub.className = "sub";
      sub.textContent = g;
      card.appendChild(sub);
      for (const { def } of allBindings()) {
        if (def.group !== g) continue;
        const key = draft[def.id];
        const r = document.createElement("div");
        r.className = "r";
        const lab = document.createElement("span");
        lab.className = "lab";
        lab.textContent = def.label;
        const chip = document.createElement("button");
        chip.className = "rebind";
        if (listeningId === def.id) {
          chip.classList.add("listening");
          chip.textContent = "…";
        } else if (key) {
          chip.textContent = key.toUpperCase();
        } else {
          chip.classList.add("unbound");
          chip.textContent = "unbound";
        }
        chip.addEventListener("click", () => {
          listeningId = listeningId === def.id ? null : def.id;
          rebuild();
        });
        r.append(lab, chip);
        card.appendChild(r);
      }
    }

    const reset = document.createElement("button");
    reset.className = "reset";
    reset.textContent = "Reset to defaults";
    reset.title = "Stage the default keys (applies when you Save)";
    reset.addEventListener("click", () => {
      draft = defaultBindings();
      listeningId = null;
      rebuild();
    });
    card.appendChild(reset);

    // Fixed reference sections (non-editable).
    for (const sec of opts.fixed) {
      const sub = document.createElement("div");
      sub.className = "sub";
      sub.textContent = sec.group;
      card.appendChild(sub);
      for (const it of sec.items) {
        const r = document.createElement("div");
        r.className = "r";
        const lab = document.createElement("span");
        lab.className = "lab";
        lab.textContent = it.label;
        r.append(lab, mkChip(it.keys));
        card.appendChild(r);
      }
    }
  };

  function close(): void {
    if (!openRoot) return;
    openRoot.remove();
    document.removeEventListener("keydown", onKey, true);
    openRoot = null;
  }

  // Capture-phase so the app's global shortcuts never fire behind the overlay.
  // While listening for a rebind, the next printable key becomes the binding.
  const onKey = (e: KeyboardEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (listeningId) {
      if (e.key === "Escape") {
        listeningId = null;
        rebuild();
      } else if (e.key === "Backspace" || e.key === "Delete") {
        draftSetBinding(draft, listeningId, "");
        listeningId = null;
        rebuild();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        draftSetBinding(draft, listeningId, e.key.toLowerCase());
        listeningId = null;
        rebuild();
      }
      return; // swallow modifier-only / other keys, keep listening
    }
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey, true);

  root.addEventListener("mousedown", (e) => {
    if (e.target === root) close(); // backdrop dismiss
  });

  rebuild();
}
